/**
 * Shared anatomy for the full-screening report surfaces (design §§8-11),
 * the dialog-bits idiom: the white-bordered "gray label + bold value" stat
 * chip (Debt Summary header + table "Total Owing" chips), the status pill
 * (paid and unpaid carry their own client-specified colours, everything else
 * falls back to neutral gray — the design's upgrade over v1's plain text;
 * unknown-status tone is a recorded extrapolation),
 * and the bordered six-column table card shared by Collections, Written-Off
 * and Bankruptcy (spec §9 proportions 17/14/13/13/12/31; header labels
 * centered, first + Remarks cells left-aligned, rows undivided).
 */
import type { ReactNode } from 'react'
import { cn, formatCurrency } from '#app/utils/misc'
import {
	CHIP_TONES,
	DETAIL_CARD,
	type PastelTone,
	SectionIcon,
} from '../primitives'

/** v1's formatCurrency idiom for cells; v2's '-' gap style for nulls. */
export const money = (value: number | null): string =>
	value == null ? '-' : formatCurrency(value)

/** White bordered label+value chip (spec §8 header chips / §9 Total Owing). */
export function StatChip({ label, value }: { label: string; value: string }) {
	return (
		<span className="inline-flex items-baseline gap-2 rounded-xl border border-[#F0F0F0] bg-[#FBFDFD] px-4 py-2.5">
			<span className="text-[17px] text-[#747474]">{label}</span>
			<span className="text-[17px] font-medium text-[#32302F]">{value}</span>
		</span>
	)
}

/** Paid / unpaid carry their own tinted-background + saturated-text pair
 * (client 2026-08-27); every other status keeps the neutral pastel. */
const STATUS_PILL_CLASSES: Record<string, string> = {
	paid: 'bg-[#F0FDF4] text-[#16A34A]',
	unpaid: 'bg-[#FFF7F7] text-[#DC2634]',
}

/** Fully-rounded status pill; '-' for missing statuses (v2 gap style). */
export function StatusPill({ status }: { status: string }) {
	if (!status) return <span className="text-gray-400">-</span>
	return (
		<span
			className={cn(
				'inline-flex rounded-full px-3 py-1 text-[13px] font-medium',
				STATUS_PILL_CLASSES[status.toLowerCase()] ??
					CHIP_TONES['neutral' satisfies PastelTone],
			)}
		>
			{status}
		</span>
	)
}

/** Spec §9 column proportions — shared by all three report tables. */
const COLUMN_WIDTHS = [
	'w-[17%]',
	'w-[14%]',
	'w-[13%]',
	'w-[13%]',
	'w-[12%]',
	'w-[31%]',
]

const CELL_ALIGN = [
	'px-4 text-center',
	'px-4 text-center',
	'px-4 text-center',
	'px-4 text-center',
	'px-4 text-center',
	'px-4 text-center',
]

export interface ReportTableRow {
	key: string | number
	cells: ReactNode[]
}

/**
 * Bordered rounded table card: plain section label, optional "Total Owing"
 * chip (the Written-Off table has none — verified in the export, spec §10),
 * page-tone ~64px header row with a 1px divider, ~60px undivided rows.
 */
export function ReportTableCard({
	title,
	iconSrc,
	owingTotal,
	columnLabels,
	rows,
}: {
	title: string
	/** Brand SVG carrying its own pastel tile, left of the title (client
	 * 2026-08-27). Omitted → the header stays title-only, as before. */
	iconSrc?: string
	/** Formatted chip value; null/omitted hides the chip. */
	owingTotal?: string | null
	columnLabels: readonly [string, string, string, string, string, string]
	rows: ReportTableRow[]
}) {
	return (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			<div className="flex flex-wrap items-center justify-between gap-4">
				<div className="flex items-center gap-3">
					{iconSrc ? <SectionIcon src={iconSrc} /> : null}
					<h2 className="text-[16px] font-semibold text-[#32302F]">{title}</h2>
				</div>
				{owingTotal != null && (
					<StatChip label="Total Owing" value={owingTotal} />
				)}
			</div>
			<div className="mt-5 overflow-hidden rounded-[14px] border border-[#F0F0F0]">
				<table className="w-full table-fixed">
					<colgroup>
						{COLUMN_WIDTHS.map((width, idx) => (
							<col key={idx} className={width} />
						))}
					</colgroup>
					<thead>
						<tr className="border-b border-[##F0F0F0] bg-[#FBFDFD]">
							{columnLabels.map((label) => (
								<th
									key={label}
									className="h-16 text-center text-[15px] font-normal text-[#747474]"
								>
									{label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr key={row.key}>
								{row.cells.map((cell, idx) => (
									<td
										key={idx}
										className={cn(
											// Wrapped remarks need a little room under the last
											// line — a fixed 60px row clips that text.
											'min-h-[60px] py-1.5 text-[15px] leading-relaxed text-[#32302F]',
											CELL_ALIGN[idx],
										)}
									>
										{cell}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	)
}
